import React, { useState, useEffect, useRef } from 'react';
import { Close } from '@carbon/icons-react';
import axios, { CancelTokenSource } from 'axios';
import useFiles from '@hooks/useFiles';
import useStyles from './useStyles';
import { useSnackbar } from '@components/Provider';
import { CapaQuotationChatRoomSharedFiles } from '@generated/graphql';

interface Props {
  currentItem?: CapaQuotationChatRoomSharedFiles;
  onComplete: () => void;
}

const ViewerDownloadSnackbar = ({ currentItem, onComplete }: Props) => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const source = useRef<CancelTokenSource | null>(null);
  const fileName =
    currentItem?.fileName && currentItem?.fileName.length > 20
      ? currentItem?.fileName.slice(0, 17) +
        '...' +
        `${currentItem?.fileName
          .slice(currentItem?.fileName.lastIndexOf('.') + 1)
          .toLowerCase()}`
      : currentItem?.fileName;

  const [value, setValue] = useState(0);
  const [error, setError] = useState(false);
  const [innerComplete, setInnerComplete] = useState(false);
  const { onDownloadFile } = useFiles();

  const onCancel = () => {
    if (source.current) {
      source.current.cancel('cancel');
      onComplete();
    }
  };

  useEffect(() => {
    if (currentItem) {
      (async () => {
        source.current = axios.CancelToken.source();

        try {
          await onDownloadFile(
            {
              fileId: currentItem?.fileId ?? '',
              fileName: currentItem?.fileName ?? ''
            },

            {
              cancelToken: source.current.token,
              onDownloadProgress: (e) => {
                if (!currentItem?.quotationSharedFileSize) return;
                const percent = Math.round(
                  (e.loaded * 100) / currentItem?.quotationSharedFileSize
                );

                setValue(percent);

                if (percent === 100) {
                  setInnerComplete(true);
                }
              }
            }
          );

          return enqueueSnackbar(
            `${fileName}  파일을 다운로드 완료하였습니다.`,
            {
              variant: 'success',
              onClose: () => {
                setInnerComplete(false);
              }
            }
          );
        } catch (e) {
          setError(true);

          return enqueueSnackbar(
            e?.message === 'cancel'
              ? `${currentItem.fileName} 파일을 다운로드 취소하였습니다.`
              : `${currentItem.fileName} 파일을 다운로드 실패하였습니다.`,
            {
              variant: 'error',
              onClose: () => {
                setError(false);
              }
            }
          );
        } finally {
          onComplete();
          setValue(0);
        }
      })();
    }
  }, [currentItem, fileName, enqueueSnackbar, onDownloadFile, onComplete]);

  if (error) return null;

  if (innerComplete) return null;

  if (!currentItem) return null;

  return (
    <div className={classes['Viewer-Download-Snackbar']}>
      <div className="content">
        {fileName}
        &nbsp; 파일 다운로드 중입니다. ({value}%)
      </div>
      <div className="close" onClick={onCancel}>
        <span>취소</span>
        <Close />
      </div>
    </div>
  );
};

export default ViewerDownloadSnackbar;
